/*
  GLYRIS maison layer: motion and small interactive components.
  No dependencies. Every effect is skipped when the visitor prefers reduced motion,
  and content is never hidden unless this file has actually run (see the head failsafe in theme.liquid).
*/
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  // touch scrolling is fast and jumpy, so scroll-linked effects only run with a mouse or trackpad
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  window.__glyrisReady = true;

  const store = {
    get(key) {
      try {
        return JSON.parse(window.localStorage.getItem(key));
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        /* private mode or storage blocked: the feature still works for this visit */
      }
    },
  };

  const session = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (error) {
        /* ignore */
      }
    },
    remove(key) {
      try {
        window.sessionStorage.removeItem(key);
      } catch (error) {
        /* ignore */
      }
    },
  };

  /* ------------------------------------------------------------------------ */
  /* Shared pause button (announcement, marquees)                             */
  /* ------------------------------------------------------------------------ */

  function bindToggle(host, onChange) {
    const toggle = host.querySelector(':scope > .g-toggle, :scope > .g-announce__toggle');
    const sync = (paused) => {
      host.classList.toggle('is-paused', paused);
      if (!toggle) return;
      toggle.setAttribute('aria-pressed', String(paused));
      toggle.setAttribute('aria-label', paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
    };
    toggle?.addEventListener('click', () => {
      const paused = !host.classList.contains('is-paused');
      sync(paused);
      onChange(paused);
    });
    return { sync, toggle };
  }

  /* ------------------------------------------------------------------------ */
  /* Scroll reveals                                                           */
  /* ------------------------------------------------------------------------ */

  const revealSelector = '[data-reveal], [data-curtain]';
  let revealObserver = null;
  let curtainObserver = null;
  // watched element -> the curtains it reveals
  const curtainsByWatcher = new Map();

  function revealAll(scope = document) {
    scope.querySelectorAll(revealSelector).forEach((el) => el.classList.add('is-in'));
  }

  function observeReveals(scope = document) {
    if (!root.classList.contains('g-motion') || designMode || !('IntersectionObserver' in window)) {
      revealAll(scope);
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
      );
    }

    // A curtain is clipped to nothing until it opens, and Chrome measures a fully clipped element as
    // zero-sized, so it never crosses a threshold. Watch its unclipped parent instead, with a zero
    // threshold so even a parent taller than the screen triggers.
    if (!curtainObserver) {
      curtainObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            curtainsByWatcher.get(entry.target)?.forEach((curtain) => curtain.classList.add('is-in'));
            curtainsByWatcher.delete(entry.target);
            curtainObserver.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0 }
      );
    }

    scope.querySelectorAll(revealSelector).forEach((el) => {
      if (el.classList.contains('is-in')) return;
      if (!el.hasAttribute('data-curtain')) {
        revealObserver.observe(el);
        return;
      }
      const watcher = el.parentElement || el;
      if (!curtainsByWatcher.has(watcher)) curtainsByWatcher.set(watcher, new Set());
      curtainsByWatcher.get(watcher).add(el);
      curtainObserver.observe(watcher);
    });
  }

  /* ------------------------------------------------------------------------ */
  /* Hero entrance                                                            */
  /* ------------------------------------------------------------------------ */

  function startHeroes(scope = document) {
    scope.querySelectorAll('.g-hero').forEach((hero) => {
      const show = () => hero.classList.add('is-loaded');
      requestAnimationFrame(() => requestAnimationFrame(show));
      // animation frames pause in background tabs; never leave the headline hidden
      window.setTimeout(show, 120);
      bindHeroVideo(hero);
      bindTrustBox(hero);
    });
  }

  // Trustpilot's script loads only when a hero shows a TrustBox. Once it is in, boxes from a
  // section re-rendered in the theme editor are drawn again by hand.
  function bindTrustBox(hero) {
    const boxes = hero.querySelectorAll('.trustpilot-widget');
    if (!boxes.length) return;
    if (window.Trustpilot) {
      boxes.forEach((box) => window.Trustpilot.loadFromElement(box, true));
      return;
    }
    if (document.querySelector('script[data-g-trustbox]')) return;
    const script = document.createElement('script');
    script.src = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
    script.async = true;
    script.dataset.gTrustbox = '';
    document.head.append(script);
  }

  // The background video stays still for visitors who ask for reduced motion.
  function bindHeroVideo(hero) {
    if (!reduceMotion.matches) return;
    hero.querySelectorAll('video').forEach((video) => {
      video.removeAttribute('autoplay');
      video.pause();
    });
  }

  /* ------------------------------------------------------------------------ */
  /* Announcement rotator                                                     */
  /* ------------------------------------------------------------------------ */

  class GlyrisAnnounce extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.g-announce__item')];
      this.index = 0;
      this.dataset.count = String(this.items.length);
      const toggle = this.querySelector('.g-announce__toggle');
      if (this.items.length < 2) {
        if (toggle) toggle.hidden = true;
        return;
      }
      this.items[0].classList.add('is-active');
      this.paused = reduceMotion.matches;
      const { sync } = bindToggle(this, (paused) => (this.paused = paused));
      sync(this.paused);

      this.addEventListener('mouseenter', () => (this.hovering = true));
      this.addEventListener('mouseleave', () => (this.hovering = false));
      this.addEventListener('focusin', () => (this.hovering = true));
      this.addEventListener('focusout', () => (this.hovering = false));

      const speed = Number(this.dataset.speed || 5) * 1000;
      this.timer = window.setInterval(() => {
        if (this.paused || this.hovering || document.hidden) return;
        this.show(this.index + 1);
      }, speed);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    show(next) {
      this.items[this.index].classList.remove('is-active');
      this.index = (next + this.items.length) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }
  }

  /* ------------------------------------------------------------------------ */
  /* Category index: hovering a name swaps the preview image and caption      */
  /* ------------------------------------------------------------------------ */

  class GlyrisIndex extends HTMLElement {
    connectedCallback() {
      this.rows = [...this.querySelectorAll('.g-index__row')];
      this.images = [...this.querySelectorAll('.g-index__preview-img')];
      this.captions = [...this.querySelectorAll('.g-index__caption-item')];
      this.rows.forEach((row, i) => {
        row.addEventListener('mouseenter', () => this.activate(i));
        row.addEventListener('focus', () => this.activate(i));
      });
      this.activate(0);
    }

    activate(i) {
      [this.rows, this.images, this.captions].forEach((list) =>
        list.forEach((el, n) => el.classList.toggle('is-active', n === i))
      );
    }
  }

  /* ------------------------------------------------------------------------ */
  /* Product rail: moves one piece at a time, by arrows or on its own         */
  /* ------------------------------------------------------------------------ */

  class GlyrisRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.g-rail__track');
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.progress = this.querySelector('.g-rail__progress');
      if (!this.track) return;

      this.prev?.addEventListener('click', () => this.userStep(-1));
      this.next?.addEventListener('click', () => this.userStep(1));

      let ticking = false;
      this.track.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            this.update();
            ticking = false;
          });
        },
        { passive: true }
      );
      window.addEventListener('resize', () => this.update(), { passive: true });
      this.bindDrag();
      this.update();
      this.setupAutoplay();
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
      this.io?.disconnect();
    }

    get stride() {
      const items = this.track.querySelectorAll('.g-rail__item');
      if (items.length > 1) return items[1].offsetLeft - items[0].offsetLeft;
      return items[0] ? items[0].getBoundingClientRect().width : this.track.clientWidth;
    }

    get max() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    // Exactly one piece per step; past the last piece it returns to the first.
    step(direction) {
      const stride = this.stride;
      const current = Math.round(this.track.scrollLeft / stride);
      let target = (current + direction) * stride;
      if (target > this.max + 2) target = 0;
      if (target < -2) target = this.max;
      this.track.scrollTo({ left: Math.min(target, this.max), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    userStep(direction) {
      this.restUntil = Date.now() + 9000;
      this.step(direction);
    }

    setupAutoplay() {
      if (this.dataset.autoplay !== 'true' || reduceMotion.matches || designMode) return;
      const interval = Math.max(2, Number(this.dataset.interval || 4)) * 1000;
      const hold = () => (this.holding = true);
      const release = () => (this.holding = false);
      this.addEventListener('mouseenter', hold);
      this.addEventListener('mouseleave', release);
      this.addEventListener('focusin', hold);
      this.addEventListener('focusout', release);
      this.track.addEventListener('touchstart', () => (this.restUntil = Date.now() + 9000), { passive: true });
      this.track.addEventListener('wheel', () => (this.restUntil = Date.now() + 9000), { passive: true });

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => (this.visible = entries[0].isIntersecting), { threshold: 0.4 });
        this.io.observe(this);
      } else {
        this.visible = true;
      }

      this.timer = window.setInterval(() => {
        if (!this.visible || this.holding || this.dragging || document.hidden) return;
        if (this.restUntil && Date.now() < this.restUntil) return;
        if (this.max <= 0) return;
        this.step(1);
      }, interval);
    }

    update() {
      const max = this.max;
      const ratio = max > 0 ? this.track.scrollLeft / max : 1;
      const visible = max > 0 ? this.track.clientWidth / this.track.scrollWidth : 1;
      if (this.progress) {
        this.progress.style.setProperty('--p', String(Math.min(1, visible + (1 - visible) * ratio)));
        this.progress.hidden = max <= 0;
      }
      const scrollable = max > 2;
      if (this.prev) this.prev.disabled = !scrollable;
      if (this.next) this.next.disabled = !scrollable;
    }

    bindDrag() {
      let startX = 0;
      let startLeft = 0;
      let moved = false;
      let active = false;

      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        active = true;
        moved = false;
        startX = event.clientX;
        startLeft = this.track.scrollLeft;
      });

      window.addEventListener('pointermove', (event) => {
        if (!active) return;
        const delta = event.clientX - startX;
        if (!moved && Math.abs(delta) > 6) {
          moved = true;
          this.dragging = true;
          this.track.classList.add('is-dragging');
        }
        if (moved) this.track.scrollLeft = startLeft - delta;
      });

      window.addEventListener('pointerup', () => {
        if (!active) return;
        active = false;
        if (!moved) return;
        this.restUntil = Date.now() + 9000;
        // settle on the nearest piece after a drag
        const stride = this.stride;
        const target = Math.round(this.track.scrollLeft / stride) * stride;
        this.track.classList.remove('is-dragging');
        this.dragging = false;
        this.track.scrollTo({ left: Math.min(target, this.max), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      });

      this.track.addEventListener('dragstart', (event) => event.preventDefault());
    }
  }

  /* ------------------------------------------------------------------------ */
  /* Marquee: seamless loop, optional scroll-reactive speed and direction     */
  /* ------------------------------------------------------------------------ */

  // Driven frame by frame rather than by a CSS or Web Animations loop, so it can change speed and
  // direction smoothly, never restarts from the beginning, and keeps its place through resizes.
  class GlyrisMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.g-marquee__track');
      this.group = this.querySelector('.g-marquee__group');
      if (!this.track || !this.group) return;

      this.speed = Math.max(5, Number(this.dataset.speed || 40)); // pixels per second
      this.sign = this.dataset.direction === 'right' ? -1 : 1;
      this.offset = 0;
      this.rate = 1; // eased towards the target rate each frame
      this.boost = 0;
      this.distance = 0;
      this.userPaused = false;

      if (reduceMotion.matches) {
        this.classList.add('is-static');
        this.querySelector(':scope > .g-toggle')?.setAttribute('hidden', '');
        return;
      }

      const { sync } = bindToggle(this, (paused) => {
        this.userPaused = paused;
        this.wake();
      });
      this.syncToggle = sync;

      if (this.dataset.hover === 'pause') {
        const set = (on) => {
          this.hovering = on;
          this.wake();
        };
        this.addEventListener('mouseenter', () => set(true));
        this.addEventListener('mouseleave', () => set(false));
        this.addEventListener('focusin', () => set(true));
        this.addEventListener('focusout', () => set(false));
      }

      this.build();
      // rebuild only when a width actually changes (fonts arriving, a real resize),
      // not on the height-only resizes phones fire while their address bar slides away
      this.widths = this.measure();
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => {
          const widths = this.measure();
          if (Math.abs(widths[0] - this.widths[0]) < 1 && Math.abs(widths[1] - this.widths[1]) < 1) return;
          this.widths = widths;
          this.build();
        });
        this.ro.observe(this.group);
        this.ro.observe(this);
      }
      document.fonts?.ready.then(() => this.build());
      this.onVisibility = () => this.wake();
      document.addEventListener('visibilitychange', this.onVisibility);

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => {
          this.visible = entries[0].isIntersecting;
          if (this.visible && this.dataset.reactive === 'true' && finePointer.matches) this.listenScroll();
          this.wake();
        });
        this.io.observe(this);
      } else {
        this.visible = true;
        this.wake();
      }
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.ro?.disconnect();
      cancelAnimationFrame(this.raf);
      this.raf = 0;
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    measure() {
      return [this.group.getBoundingClientRect().width, this.clientWidth];
    }

    build() {
      this.track.querySelectorAll('[data-clone]').forEach((clone) => clone.remove());
      const width = this.group.getBoundingClientRect().width;
      if (!width) return;

      // enough copies to cover the widest screen twice over, so the loop never shows a gap
      const copies = Math.max(1, Math.ceil((Math.max(window.innerWidth, this.clientWidth) * 2) / width));
      for (let i = 0; i < copies; i += 1) {
        const clone = this.group.cloneNode(true);
        clone.setAttribute('data-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        [clone, ...clone.querySelectorAll('*')].forEach((el) => {
          [...el.attributes].forEach((attr) => {
            if (attr.name.startsWith('data-shopify')) el.removeAttribute(attr.name);
          });
        });
        this.track.appendChild(clone);
      }

      // one loop = the distance from the start of a group to the start of the next, gaps included
      const next = this.group.nextElementSibling;
      this.distance = next ? next.offsetLeft - this.group.offsetLeft : width;
      this.offset = ((this.offset % this.distance) + this.distance) % this.distance;
      this.paint();
      this.dispatchEvent(new CustomEvent('g:marquee-built', { bubbles: true }));
    }

    get targetRate() {
      if (this.userPaused || !this.visible || document.hidden) return 0;
      return (this.hovering ? 0.15 : 1) + this.boost;
    }

    wake() {
      if (this.raf || !this.distance) return;
      this.last = performance.now();
      this.raf = requestAnimationFrame((time) => this.tick(time));
    }

    tick(time) {
      const dt = Math.min(64, time - this.last); // a long gap (background tab) never causes a leap
      this.last = time;
      const target = this.targetRate;
      this.rate += (target - this.rate) * Math.min(1, dt / 240);
      this.boost *= Math.pow(0.92, dt / 16.7);
      if (Math.abs(this.boost) < 0.01) this.boost = 0;

      this.offset += this.sign * this.rate * this.speed * (dt / 1000);
      this.offset = ((this.offset % this.distance) + this.distance) % this.distance;
      this.paint();

      // sleep once fully stopped; wake() starts it again
      if (target === 0 && Math.abs(this.rate) < 0.005) {
        this.rate = 0;
        this.raf = 0;
        return;
      }
      this.raf = requestAnimationFrame((next) => this.tick(next));
    }

    paint() {
      this.track.style.transform = `translate3d(${-this.offset}px, 0, 0)`;
    }

    // Scroll velocity briefly speeds the band up; scrolling back up turns it around.
    listenScroll() {
      if (this.onScroll) return;
      let lastY = window.scrollY;
      this.onScroll = () => {
        const y = window.scrollY;
        const delta = y - lastY;
        lastY = y;
        if (!this.visible) return;
        this.boost = Math.max(-3, Math.min(3, this.boost + delta * 0.02));
        this.wake();
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
  }

  /* ------------------------------------------------------------------------ */
  /* As worn: centred carousel, one clip open and playing at a time            */
  /* ------------------------------------------------------------------------ */

  class GlyrisReel extends HTMLElement {
    connectedCallback() {
      this.items = [...this.querySelectorAll('.g-reel__item')];
      this.panels = [...this.querySelectorAll('.g-reel__panel')];
      this.line = this.querySelector('.g-reel__line span');
      this.counter = this.querySelector('[data-reel-current]');
      this.pauseButton = this.querySelector('[data-reel-pause]');
      if (!this.items.length) return;

      this.index = 0;
      this.holds = new Set();
      this.static = reduceMotion.matches;
      this.classList.toggle('is-static', this.static);

      this.items.forEach((item, i) => {
        item.querySelector('.g-reel__hit')?.addEventListener('click', () => this.go(i));
        // without motion the open clip gets the browser's own controls instead of autoplay
        if (this.static) item.querySelector('video')?.setAttribute('controls', '');
      });
      this.querySelector('[data-reel-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-reel-next]')?.addEventListener('click', () => this.go(this.index + 1));
      if (this.pauseButton) {
        this.pauseButton.hidden = this.static;
        this.pauseButton.addEventListener('click', () => this.setUserPaused(!this.userPaused));
      }
      this.line?.addEventListener('animationend', () => this.go(this.index + 1));

      // pause while a mouse rests on the playing clip, or keyboard focus is inside
      const stage = this.querySelector('.g-reel__stage');
      stage?.addEventListener('pointermove', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hold('hover', Boolean(event.target.closest('.g-reel__item[data-pos="0"]')));
      });
      stage?.addEventListener('pointerleave', () => this.hold('hover', false));
      this.addEventListener('focusin', (event) => this.hold('focus', event.target.matches(':focus-visible')));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.hold('focus', false);
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.go(this.index + 1);
        else if (event.key === 'ArrowLeft') this.go(this.index - 1);
      });
      this.bindSwipe(stage);

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            this.visible = entries[0].isIntersecting;
            this.hold('offscreen', !this.visible);
            this.syncVideos();
          },
          { threshold: 0.35 }
        );
        this.io.observe(this);
      } else {
        this.visible = true;
      }

      this.onVisibility = () => this.hold('hidden', document.hidden);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.onBlockSelect = (event) => {
        const i = this.items.indexOf(event.target);
        if (i > -1) this.go(i);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.go(0, true);
    }

    disconnectedCallback() {
      this.io?.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    // a sideways swipe or mouse drag moves one clip; vertical movement still scrolls the page
    bindSwipe(stage) {
      if (!stage) return;
      let startX = null;
      let startY = 0;
      stage.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        startX = event.clientX;
        startY = event.clientY;
      });
      stage.addEventListener('pointerup', (event) => {
        if (startX === null) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        startX = null;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
        this.swiped = true;
        this.go(this.index + (dx < 0 ? 1 : -1));
        window.setTimeout(() => (this.swiped = false), 300);
      });
      stage.addEventListener('pointercancel', () => (startX = null));
      // a swipe should not also count as a click on the clip underneath
      stage.addEventListener(
        'click',
        (event) => {
          if (!this.swiped) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        },
        true
      );
      stage.addEventListener('dragstart', (event) => event.preventDefault());
    }

    hold(reason, on) {
      if (on) this.holds.add(reason);
      else this.holds.delete(reason);
      this.classList.toggle('is-held', this.holds.size > 0);
    }

    setUserPaused(paused) {
      this.userPaused = paused;
      this.hold('user', paused);
      this.classList.toggle('is-paused', paused);
      this.pauseButton.setAttribute('aria-pressed', String(paused));
      this.pauseButton.setAttribute('aria-label', paused ? this.pauseButton.dataset.labelPlay : this.pauseButton.dataset.labelPause);
      this.syncVideos();
    }

    go(i, initial = false) {
      const count = this.items.length;
      const half = Math.floor(count / 2);
      const previous = this.index;
      this.index = ((i % count) + count) % count;

      this.items.forEach((item, n) => {
        let pos = (n - this.index + count) % count;
        if (pos > half) pos -= count;
        const before = Number(item.dataset.pos);
        if (!initial && Math.abs(pos - before) > 1) item.classList.add('is-jump');
        item.dataset.pos = String(pos);
        const hit = item.querySelector('.g-reel__hit');
        if (hit) hit.hidden = pos === 0;
        const video = item.querySelector('video');
        if (video && pos === 0 && n !== previous && video.readyState > 0) video.currentTime = 0;
      });
      // let wrapped clips take their new place unseen, then fade them back in
      requestAnimationFrame(() =>
        requestAnimationFrame(() => this.items.forEach((item) => item.classList.remove('is-jump')))
      );

      this.panels.forEach((panel, n) => {
        panel.classList.toggle('is-active', n === this.index);
        panel.inert = n !== this.index;
      });
      if (this.counter) this.counter.textContent = String(this.index + 1).padStart(2, '0');

      this.syncVideos();
      if (this.static || !this.line) return;
      this.classList.remove('is-running');
      void this.line.offsetWidth; // restart the line
      this.classList.add('is-running');
    }

    // only the open clip plays, only while on screen; its neighbours load in the background
    syncVideos() {
      this.items.forEach((item) => {
        const video = item.querySelector('video');
        if (!video) return;
        const pos = Number(item.dataset.pos);
        if (pos === 0 && this.visible && !this.static && !this.userPaused) {
          if (video.preload !== 'auto') video.preload = 'auto';
          video.muted = true;
          video.play().catch(() => {});
          return;
        }
        video.pause();
        if (Math.abs(pos) === 1 && this.visible && video.preload === 'none') video.preload = 'metadata';
      });
    }
  }

  /* ------------------------------------------------------------------------ */
  /* Client reviews: one at a time, a thin line marks the time on each        */
  /* ------------------------------------------------------------------------ */

  class GlyrisReviews extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.g-reviews__slide')];
      this.segs = [...this.querySelectorAll('.g-reviews__seg')];
      if (this.slides.length < 2) return;

      this.index = 0;
      this.holds = new Set();
      this.static = reduceMotion.matches;
      this.classList.toggle('is-static', this.static);

      this.querySelector('[data-reviews-prev]')?.addEventListener('click', () => this.activate(this.index - 1));
      this.querySelector('[data-reviews-next]')?.addEventListener('click', () => this.activate(this.index + 1));
      this.segs.forEach((seg) => seg.querySelector('span')?.addEventListener('animationend', () => this.activate(this.index + 1)));

      const stage = this.querySelector('.g-reviews__stage');
      stage?.addEventListener('mouseenter', () => this.hold('hover', true));
      stage?.addEventListener('mouseleave', () => this.hold('hover', false));
      this.addEventListener('focusin', () => this.hold('focus', true));
      this.addEventListener('focusout', () => this.hold('focus', false));

      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver((entries) => this.hold('offscreen', !entries[0].isIntersecting), { threshold: 0.3 });
        this.io.observe(this);
      }

      document.addEventListener('shopify:block:select', (event) => {
        const i = this.slides.indexOf(event.target);
        if (i > -1) this.activate(i);
      });

      this.activate(0);
    }

    disconnectedCallback() {
      this.io?.disconnect();
    }

    hold(reason, on) {
      if (on) this.holds.add(reason);
      else this.holds.delete(reason);
      this.classList.toggle('is-held', this.holds.size > 0);
    }

    activate(i) {
      const count = this.slides.length;
      this.index = ((i % count) + count) % count;
      this.slides.forEach((slide, n) => {
        slide.classList.toggle('is-active', n === this.index);
        slide.inert = n !== this.index;
      });
      this.segs.forEach((seg, n) => {
        seg.classList.remove('is-running');
        seg.classList.toggle('is-active', n === this.index);
        seg.classList.toggle('is-done', n < this.index);
      });
      if (this.static) return;
      const seg = this.segs[this.index];
      if (seg) {
        void seg.offsetWidth; // restart the line
        seg.classList.add('is-running');
      }
    }
  }

  /* ------------------------------------------------------------------------ */
  /* Silver or gold comparison                                                */
  /* ------------------------------------------------------------------------ */

  class GlyrisCompare extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('.g-compare');
      this.range = this.querySelector('.g-compare__range');
      if (!this.frame || !this.range) return;

      this.range.addEventListener('input', () => {
        this.stopIntro();
        this.set(Number(this.range.value));
      });
      this.set(Number(this.range.value));

      if (reduceMotion.matches || designMode || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          io.disconnect();
          this.playIntro();
        },
        { threshold: 0.55 }
      );
      io.observe(this.frame);
    }

    set(value) {
      this.frame.style.setProperty('--pos', `${value}%`);
      this.range.value = String(value);
      this.range.setAttribute('aria-valuetext', `${Math.round(value)}%`);
    }

    // A short glide that shows the frame is interactive, then rests in the middle
    playIntro() {
      const keys = [50, 30, 66, 50];
      const duration = 2200;
      const start = performance.now();
      const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      const tick = (now) => {
        if (this.introStopped) return;
        const t = Math.min(1, (now - start) / duration);
        const scaled = t * (keys.length - 1);
        const i = Math.min(keys.length - 2, Math.floor(scaled));
        const local = ease(scaled - i);
        this.set(keys[i] + (keys[i + 1] - keys[i]) * local);
        if (t < 1) this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    }

    stopIntro() {
      this.introStopped = true;
      cancelAnimationFrame(this.raf);
    }
  }

  /* ------------------------------------------------------------------------ */
  /* Welcome offer: timed popup, side tab, code reveal after sign-up          */
  /* ------------------------------------------------------------------------ */

  const WELCOME_KEY = 'glyris-welcome';
  const WELCOME_SUBMITTED = 'glyris-welcome-submitted';

  class GlyrisWelcome extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.tab = this.querySelector('[data-welcome-open]');
      if (!this.dialog) return;

      this.querySelectorAll('[data-welcome-close]').forEach((btn) => btn.addEventListener('click', () => this.close()));
      this.tab?.addEventListener('click', () => this.open());
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      // clicking the dimmed backdrop closes the offer
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.querySelector('form')?.addEventListener('submit', () => session.set(WELCOME_SUBMITTED, '1'));
      this.querySelector('[data-welcome-copy]')?.addEventListener('click', (event) => this.copy(event.currentTarget));

      document.addEventListener('click', (event) => {
        const link = event.target.closest('a[href="#welcome"]');
        if (!link) return;
        event.preventDefault();
        this.open();
      });

      if (designMode) {
        document.addEventListener('shopify:section:select', (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.open(true);
        });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.detail.sectionId === this.dataset.sectionId) this.dialog.close();
        });
        return;
      }

      const posted = new URLSearchParams(window.location.search).get('customer_posted') === 'true';
      const submittedHere = session.get(WELCOME_SUBMITTED) === '1';
      const hasError = Boolean(this.querySelector('[data-welcome-error]'));

      if (submittedHere && (posted || hasError)) {
        session.remove(WELCOME_SUBMITTED);
        if (!hasError) {
          store.set(WELCOME_KEY, { state: 'subscribed', at: Date.now() });
          // the page reloads after any sign-up form, so keep the newsletter section quiet
          document.querySelectorAll('.g-news__msg:not(.g-news__msg--error)').forEach((msg) => (msg.hidden = true));
        }
        this.open(true);
        return;
      }

      const saved = store.get(WELCOME_KEY);
      if (saved && saved.state === 'subscribed') return;

      const days = Number(this.dataset.days || 7);
      if (saved && saved.state === 'dismissed' && Date.now() - saved.at < days * 864e5) {
        this.showTab();
        return;
      }

      this.schedule();
    }

    schedule() {
      const delay = Math.max(2, Number(this.dataset.delay || 7)) * 1000;
      this.timer = window.setTimeout(() => this.open(), delay);

      if (this.dataset.exitIntent === 'true' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        this.onLeave = (event) => {
          if (event.clientY <= 0 && !event.relatedTarget) this.open();
        };
        document.addEventListener('mouseout', this.onLeave);
      }
    }

    // Shopify's cookie banner comes first: a new visitor shouldn't face two dialogs stacked on each other
    consentPending() {
      const banner = document.getElementById('shopify-pc__banner');
      return Boolean(banner && banner.getClientRects().length && getComputedStyle(banner).visibility !== 'hidden');
    }

    afterConsent(callback) {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        document.removeEventListener('visitorConsentCollected', finish);
        window.clearInterval(poll);
        window.setTimeout(callback, 1500);
      };
      const poll = window.setInterval(() => {
        if (!this.consentPending()) finish();
      }, 1000);
      document.addEventListener('visitorConsentCollected', finish);
    }

    open(force = false) {
      window.clearTimeout(this.timer);
      if (this.onLeave) document.removeEventListener('mouseout', this.onLeave);
      if (this.dialog.open) return;
      if (!force && this.consentPending()) {
        this.afterConsent(() => this.open());
        return;
      }
      // never interrupt another open dialog (cart drawer, search) unless asked to
      if (!force && document.querySelector('dialog[open]')) {
        this.showTab();
        return;
      }
      this.hideTab();
      this.dialog.showModal();
      requestAnimationFrame(() => this.dialog.classList.add('is-open'));
    }

    close() {
      this.dialog.classList.remove('is-open');
      window.setTimeout(() => this.dialog.close(), reduceMotion.matches ? 0 : 260);
      const saved = store.get(WELCOME_KEY);
      if (!saved || saved.state !== 'subscribed') {
        store.set(WELCOME_KEY, { state: 'dismissed', at: Date.now() });
        this.showTab();
      }
    }

    showTab() {
      if (this.tab) this.tab.hidden = false;
    }

    hideTab() {
      if (this.tab) this.tab.hidden = true;
    }

    async copy(button) {
      const code = button.dataset.welcomeCopy;
      try {
        await navigator.clipboard.writeText(code);
        const label = button.textContent;
        button.textContent = button.dataset.labelDone || 'Copied';
        window.setTimeout(() => (button.textContent = label), 2000);
      } catch (error) {
        /* clipboard blocked: the code stays visible to copy by hand */
      }
    }
  }

  /* ------------------------------------------------------------------------ */
  /* Shop the edit: tabs that slide a hairline and sweep their pieces in       */
  /* ------------------------------------------------------------------------ */

  class GlyrisEdit extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
      this.list = this.querySelector('[role="tablist"]');
      this.indicator = this.querySelector('.g-edit__indicator');
      if (!this.tabs.length) return;
      this.index = 0;

      this.tabs.forEach((tab, i) => tab.addEventListener('click', () => this.select(i)));
      this.list?.addEventListener('keydown', (event) => {
        const keys = { ArrowRight: this.index + 1, ArrowLeft: this.index - 1, Home: 0, End: this.tabs.length - 1 };
        if (!(event.key in keys)) return;
        event.preventDefault();
        const count = this.tabs.length;
        this.select((keys[event.key] + count) % count, true);
      });

      this.placeIndicator(false);
      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => this.placeIndicator(false));
        this.ro.observe(this.list);
      }
      document.fonts?.ready.then(() => this.placeIndicator(false));

      // the first row sweeps in once, when the section comes into view
      if (!root.classList.contains('g-motion') || designMode || !('IntersectionObserver' in window)) {
        this.classList.add('is-seen');
      } else {
        this.io = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            this.io.disconnect();
            this.classList.add('is-seen');
            this.sweep(this.panels[this.index]);
          },
          { threshold: 0.2 }
        );
        this.io.observe(this);
      }

      this.onBlockSelect = (event) => {
        const i = this.tabs.indexOf(event.target);
        if (i > -1) this.select(i);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.ro?.disconnect();
      this.io?.disconnect();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    select(i, focus = false) {
      if (i === this.index) return;
      const previous = this.panels[this.index];
      const next = this.panels[i];
      this.index = i;

      this.tabs.forEach((tab, n) => {
        const on = n === i;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
      });
      if (focus) this.tabs[i].focus();
      this.placeIndicator(true);

      if (previous) {
        previous.classList.remove('is-active', 'is-entering');
        previous.hidden = true;
      }
      if (!next) return;
      next.hidden = false;
      next.classList.add('is-active');
      const track = next.querySelector('.g-rail__track');
      if (track) track.scrollLeft = 0;
      next.querySelector('g-rail')?.update?.();
      this.sweep(next);
    }

    sweep(panel) {
      if (!panel || reduceMotion.matches) return;
      panel.classList.remove('is-entering');
      void panel.offsetWidth; // restart the animation
      panel.classList.add('is-entering');
    }

    placeIndicator(animate) {
      const tab = this.tabs[this.index];
      if (!tab || !this.indicator) return;
      this.indicator.classList.toggle('is-instant', !animate);
      this.indicator.style.setProperty('--x', `${tab.offsetLeft}px`);
      this.indicator.style.setProperty('--w', `${tab.offsetWidth}px`);
      // keep the chosen tab in view when the tabs scroll sideways on small screens
      if (this.list && this.list.scrollWidth > this.list.clientWidth) {
        this.list.scrollTo({
          left: tab.offsetLeft - (this.list.clientWidth - tab.offsetWidth) / 2,
          behavior: animate && !reduceMotion.matches ? 'smooth' : 'auto',
        });
      }
    }
  }

  /* ------------------------------------------------------------------------ */
  /* Complete the look: one set at a time, its pieces dealt out like cards     */
  /* ------------------------------------------------------------------------ */

  class GlyrisSets extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.g-sets__slide')];
      this.counter = this.querySelector('[data-sets-current]');
      if (!this.slides.length) return;
      this.index = 0;

      this.querySelector('[data-sets-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-sets-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.addEventListener('keydown', (event) => {
        if (event.target.closest('select')) return;
        if (event.key === 'ArrowRight') this.go(this.index + 1);
        else if (event.key === 'ArrowLeft') this.go(this.index - 1);
      });

      // pointing at a piece in the list lifts its card in the fan, and the other way round
      this.slides.forEach((slide) => {
        const light = (piece) =>
          slide.querySelectorAll('[data-piece]').forEach((el) => el.classList.toggle('is-lit', el.dataset.piece === piece));
        slide.querySelectorAll('[data-piece]').forEach((el) => {
          el.addEventListener('pointerenter', () => light(el.dataset.piece));
          el.addEventListener('pointerleave', () => light(null));
          el.addEventListener('focusin', () => light(el.dataset.piece));
          el.addEventListener('focusout', () => light(null));
        });
      });

      this.bindSwipe(this.querySelector('.g-sets__viewport'));

      // the first set is dealt when the section comes into view
      if (!root.classList.contains('g-motion') || designMode || !('IntersectionObserver' in window)) {
        this.classList.add('is-seen');
      } else {
        this.io = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            this.io.disconnect();
            this.classList.add('is-seen');
          },
          { threshold: 0.25 }
        );
        this.io.observe(this);
      }

      this.onBlockSelect = (event) => {
        const i = this.slides.indexOf(event.target);
        if (i > -1) this.go(i);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.io?.disconnect();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    bindSwipe(viewport) {
      if (!viewport) return;
      let startX = null;
      let startY = 0;
      viewport.addEventListener('pointerdown', (event) => {
        if (event.target.closest('select, button, input')) return;
        startX = event.clientX;
        startY = event.clientY;
      });
      viewport.addEventListener('pointerup', (event) => {
        if (startX === null) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        startX = null;
        if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.3) return;
        this.swiped = true;
        this.go(this.index + (dx < 0 ? 1 : -1));
        window.setTimeout(() => (this.swiped = false), 300);
      });
      viewport.addEventListener('pointercancel', () => (startX = null));
      viewport.addEventListener(
        'click',
        (event) => {
          if (!this.swiped) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        },
        true
      );
    }

    go(i) {
      const count = this.slides.length;
      const next = ((i % count) + count) % count;
      if (next === this.index) return;
      const direction = (i > this.index) ? 1 : -1;
      this.style.setProperty('--dir', String(direction));
      this.index = next;
      this.slides.forEach((slide, n) => {
        const on = n === next;
        slide.classList.toggle('is-active', on);
        slide.inert = !on;
      });
      if (this.counter) this.counter.textContent = String(next + 1).padStart(2, '0');
    }
  }

  /* ------------------------------------------------------------------------ */
  /* Why GLYRIS: the frame shows the piece behind the reason in view           */
  /* ------------------------------------------------------------------------ */

  class GlyrisWhy extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.g-why__slide')];
      this.reasons = [...this.querySelectorAll('.g-why__reason')];
      this.dots = [...this.querySelectorAll('[data-why-go]')];
      this.stage = this.querySelector('.g-why__stage');
      this.counter = this.querySelector('[data-why-current]');
      if (this.reasons.length < 2) return;
      this.index = 0;
      this.jumpUntil = 0;
      // matches the CSS breakpoint where the frame moves from beside the reasons to above them
      this.wide = window.matchMedia('(min-width: 990px)');
      this.classList.add('is-ready');

      // wide screens: whichever reason crosses a line just below the middle of the screen leads, so the
      // piece changes as its heading rises into view
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            if (!this.wide.matches) return;
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.show(this.reasons.indexOf(entry.target));
            });
          },
          { rootMargin: '-60% 0px -40% 0px' }
        );
        this.reasons.forEach((reason) => this.io.observe(reason));

        this.nearIo = new IntersectionObserver((entries) => {
          this.near = entries[0].isIntersecting;
          this.onScroll();
        });
        this.nearIo.observe(this);
      } else {
        this.near = true;
      }

      // narrow screens: the frame pins to the top and the reasons scroll up beneath it; whichever reason
      // has risen to the middle of the space left under the frame leads
      this.onScroll = () => {
        if (this.wide.matches || !this.near || this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          if (Date.now() < this.jumpUntil) return;
          const pinned = this.pinnedBottom();
          const line = pinned + (window.innerHeight - pinned) / 2;
          let next = 0;
          this.reasons.forEach((reason, i) => {
            if (reason.getBoundingClientRect().top <= line) next = i;
          });
          this.show(next);
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });

      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.whyGo))));
      this.reasons.forEach((reason, i) => reason.addEventListener('focusin', () => this.show(i)));

      this.onBlockSelect = (event) => {
        const i = this.reasons.indexOf(event.target);
        if (i > -1) this.go(i);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }

    disconnectedCallback() {
      this.io?.disconnect();
      this.nearIo?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    // where the bottom of the frame sits once it has pinned, in pixels from the top of the screen
    pinnedBottom() {
      return (parseFloat(getComputedStyle(this.stage).top) || 0) + this.stage.offsetHeight;
    }

    go(i) {
      const next = Math.max(0, Math.min(i, this.reasons.length - 1));
      const behavior = reduceMotion.matches ? 'auto' : 'smooth';
      if (this.wide.matches) {
        this.reasons[next].scrollIntoView({ block: 'center', behavior });
      } else {
        // bring the reason up to just under the pinned frame, without flicking through the pieces on the way
        this.jumpUntil = Date.now() + 900;
        const top = this.reasons[next].getBoundingClientRect().top - this.pinnedBottom();
        // Horizon's scroll-up header shows on the way up and hides on the way down, moving the frame with it
        const header = document.querySelector("#header-component[sticky='scroll-up']");
        let shift = 0;
        if (header) {
          const showing = header.dataset.stickyState === 'active';
          if (top < 0 && !showing) shift = header.offsetHeight;
          if (top > 0 && showing) shift = -header.offsetHeight;
        }
        window.scrollTo({ top: window.scrollY + top - shift, behavior });
      }
      this.show(next);
    }

    show(i) {
      if (i < 0 || i >= this.reasons.length || i === this.index) return;
      this.index = i;
      this.slides.forEach((slide, n) => {
        slide.classList.toggle('is-active', n === i);
        slide.inert = n !== i;
      });
      this.reasons.forEach((reason, n) => reason.classList.toggle('is-active', n === i));
      this.dots.forEach((dot, n) => dot.setAttribute('aria-current', String(n === i)));
      if (this.counter) this.counter.textContent = String(i + 1).padStart(2, '0');
    }
  }

  /* ------------------------------------------------------------------------ */
  /* Add to bag from cards and sets, without leaving the page                  */
  /* ------------------------------------------------------------------------ */

  // Any form with data-glyris-add posts to the cart in the background, then tells Horizon's cart
  // drawer, bag count and cart items through the same cart:update event its product form uses.
  const cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function setButtonState(button, state, text) {
    const label = button.querySelector('[data-label]') || button;
    if (!button.dataset.labelIdle) button.dataset.labelIdle = label.textContent.trim();
    button.classList.remove('is-busy', 'is-added', 'is-error');
    if (state) button.classList.add(`is-${state}`);
    label.textContent = text || button.dataset.labelIdle;
  }

  async function addToCart(form) {
    const button = form.querySelector('[type="submit"]');
    if (!button || button.classList.contains('is-busy')) return;
    const formData = new FormData(form);
    const sections = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);
    if (sections.length) formData.append('sections', sections.join(','));

    setButtonState(button, 'busy');
    try {
      const response = await fetch(`${cartRoot}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const added = await response.json();
      if (!response.ok || added.status) throw new Error(added.description || added.message || 'Could not add');
      const cart = await (await fetch(`${cartRoot}cart.js`)).json();
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: form.id || 'glyris-add',
            data: { source: 'glyris-add', itemCount: cart.item_count, sections: added.sections },
          },
        })
      );
      setButtonState(button, 'added', form.dataset.labelAdded || 'Added');
    } catch (error) {
      setButtonState(button, 'error', error.message);
    }
    window.setTimeout(() => setButtonState(button, null), 2400);
  }

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-glyris-add]');
    if (!form) return;
    event.preventDefault();
    addToCart(form);
  });

  const define = (name, ctor) => {
    if (!customElements.get(name)) customElements.define(name, ctor);
  };

  define('g-announce', GlyrisAnnounce);
  define('g-index', GlyrisIndex);
  define('g-rail', GlyrisRail);
  define('g-compare', GlyrisCompare);
  define('g-reel', GlyrisReel);
  define('g-reviews', GlyrisReviews);
  define('g-marquee', GlyrisMarquee);
  define('g-welcome', GlyrisWelcome);
  define('g-edit', GlyrisEdit);
  define('g-sets', GlyrisSets);
  define('g-why', GlyrisWhy);

  /* ------------------------------------------------------------------------ */
  /* Boot, and keep working inside the theme editor                           */
  /* ------------------------------------------------------------------------ */

  function boot(scope = document) {
    observeReveals(scope);
    startHeroes(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));

  reduceMotion.addEventListener?.('change', (event) => {
    if (event.matches) {
      root.classList.remove('g-motion');
      revealAll();
    }
  });
})();
